// ─── Environment configuration ────────────────────────────────────────────────

import { z } from 'zod'
import { readRuntimeConfig } from './runtime-config'


const envSchema = z.object({
  VITE_APP_ENV: z.enum(['development', 'sandbox', 'production']).default('development'),
  VITE_APP_NAME: z.string().trim().min(1).default('Menu Builder AI'),

  /**
   * The backend every call in `src/api` goes to. In development requests go through the Vite proxy (same origin, no CORS), see
   * `VITE_API_USE_PROXY`; the deployed app calls this URL directly, so the backend must allow its origin via CORS.
   */
  VITE_API_BASE_URL: z.string().trim().default(''),
  /** Development only: send `/api` calls to the Vite dev server, which forwards them to `VITE_API_PROXY_TARGET`. */
  VITE_API_USE_PROXY: z.enum(['true', 'false']).default('false'),


  VITE_API_TIMEOUT_MS: z.coerce.number().int().positive().default(30_000),

  /** Until login is wired up: the user id sent as X-User-Id. Must be an active user in the API. */
  VITE_DEV_USER_ID: z.string().trim().optional(),
})


const parsed = envSchema.safeParse({ ...import.meta.env, ...readRuntimeConfig() })

if (!parsed.success) {
  throw new Error(`Invalid environment configuration:\n${z.prettifyError(parsed.error)}`)
}

const config = parsed.data

export const env = {
  appEnv: config.VITE_APP_ENV,
  appName: config.VITE_APP_NAME,
  apiBaseUrl: config.VITE_API_BASE_URL.replace(/\/+$/, ''),
  apiUseProxy: config.VITE_API_USE_PROXY === 'true',
  apiTimeoutMs: config.VITE_API_TIMEOUT_MS,
  devUserId: config.VITE_DEV_USER_ID || null,
  isProduction: config.VITE_APP_ENV === 'production',
} as const

export type AppEnv = typeof env
